import { Fragment, useEffect, useState } from "react";
import Footer from "../components/footer";
import {
  Accordion,
  AccordionHeader,
  AccordionBody,
  Typography,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
} from "@material-tailwind/react";
import { API_URL } from "../App.js";
import { GiDna2, GiProcessor, GiMicroscope, GiArtificialIntelligence, GiAtom } from "react-icons/gi";
import { Link, useParams } from "react-router-dom";

function Icon({ open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={`h-5 w-5 transition-transform ${open ? "rotate-180" : ""}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

const defaultImage = process.env.PUBLIC_URL + "Logo_AdaLab.png";

const truncateText = (text, maxLength) => {
  if (text.length <= maxLength) {
    return text;
  }
  return text.substring(0, maxLength) + "...";
};

// Iconos dinámicos por índice (fallback si no hay imagen)
const iconos = [GiDna2, GiProcessor, GiMicroscope, GiArtificialIntelligence, GiAtom];


const getIconOrImage = (tema, index) => {
  if (tema.icono && tema.icono !== null) {
    return (
      <img
        src={tema.icono}
        alt={tema.nombre}
        className="w-6 h-6 object-cover rounded-full"
      />
    );
  }
  const IconComponent = iconos[index % iconos.length];
  return <IconComponent className="w-6 h-6" />;
};

const Projects = () => {
  const { temaId } = useParams();
  const [open, setOpen] = useState({});
  const [data, setData] = useState(null);
  const [selectedTema, setSelectedTema] = useState(null);

  const handleOpen = (temaId, subtemaId) => {
    setOpen((prevOpen) => {
      const isSubtemaOpen = prevOpen[temaId]?.includes(subtemaId);

      if (isSubtemaOpen) {
        return {
          ...prevOpen,
          [temaId]: prevOpen[temaId].filter((id) => id !== subtemaId),
        };
      } else {
        return {
          ...prevOpen,
          [temaId]: [...(prevOpen[temaId] || []), subtemaId],
        };
      }
    });
  };

  useEffect(() => {
    fetch(API_URL + "investigacion")
      .then((response) => response.json())
      .then((responseData) => {
        const initialOpenState = {};
        responseData.data.tema.forEach((tema) => {
          initialOpenState[tema.id] = [null];
        });
        setOpen(initialOpenState);
        setData(responseData.data);
        // Seleccionar el tema de la URL o "Todos" por defecto
        if (temaId) {
          const temaIdNum = parseInt(temaId);
          const temaExiste = responseData.data.tema.find(t => t.id === temaIdNum);
          if (temaExiste) {
            setSelectedTema(temaIdNum);
          } else {
            setSelectedTema('todos');
          }
        } else {
          setSelectedTema('todos');
        }
      })
      .catch((error) => console.error(error));
  }, [temaId]);

  // Cerrar todos los acordeones cuando cambia el tema seleccionado
  useEffect(() => {
    if (data) {
      const resetOpenState = {};
      data.tema.forEach((tema) => {
        resetOpenState[tema.id] = [];
      });
      setOpen(resetOpenState);
    }
  }, [selectedTema, data]);

  if (!data) {
    return (
      <>
        <br />
        <Typography variant="h3" className="font-bold font-sans" color="black">
          Loading...
        </Typography>
      </>
    );
  }

  const temaActual = data.tema.find(t => t.id === selectedTema);
  const temaIndex = data.tema.findIndex(t => t.id === selectedTema);

  return (
    <div className="flex flex-col flex-grow bg-gray-50">
      <br />

      {/* Tabs de navegación de temas */}
      <div className="flex flex-wrap justify-center gap-2 mb-6 mx-4">
        {/* Tab Todos */}
        <button
          onClick={() => setSelectedTema('todos')}
          className={`flex items-center gap-1 px-4 py-2 rounded-full font-sans text-sm font-medium transition-all duration-300 ${selectedTema === 'todos'
              ? 'bg-colorPrincipal text-white shadow-md scale-105'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
        >
          <span>Todos</span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${selectedTema === 'todos'
              ? "bg-white/20"
              : "bg-gray-300"
            }`}>
            {data.subtema.length}
          </span>
        </button>
        {data.tema.map((tema, index) => {
          const isActive = selectedTema === tema.id;
          const subtemasCount = data.subtema.filter(s => s.tema_id === tema.id).length;
          return (
            <button
              key={tema.id}
              onClick={() => setSelectedTema(tema.id)}
              className={`flex items-center gap-1 px-4 py-2 rounded-full font-sans text-sm font-medium transition-all duration-300 ${isActive
                  ? 'bg-colorPrincipal text-white shadow-md scale-105'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              {getIconOrImage(tema, index)}
              <span>{tema.nombre}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${isActive
                  ? "bg-white/20"
                  : "bg-gray-300"
                }`}>
                {subtemasCount}
              </span>
            </button>
          );
        })}
      </div>

      {/* Contenido del tema seleccionado */}
      <div className="container mx-auto px-4 py-8">
        {selectedTema === 'todos' ? (
          // Mostrar todos los temas
          data.tema.map((tema, temaIdx) => (
            <div key={tema.id} className="mb-8 pb-6 border-b border-gray-300 last:border-b-0">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-12 h-12 rounded-full bg-colorPrincipal flex items-center justify-center text-white shadow-md overflow-hidden">
                  {getIconOrImage(tema, temaIdx)}
                </div>
                <Typography variant="h4" className="font-extrabold font-sans text-gray-800">
                  {tema.nombre}
                </Typography>
              </div>

              <div className="space-y-2">
                {data.subtema
                  .filter((subtema) => subtema.tema_id === tema.id)
                  .map((subtema) => {
                    const proyectosCount = data.proyecto.filter(p => p.subtema_id === subtema.id).length;
                    return (
                      <Accordion
                        key={subtema.id}
                        open={open[tema.id]?.includes(subtema.id)}
                        icon={<Icon open={open[tema.id]?.includes(subtema.id)} />}
                        className="bg-white rounded-xl shadow-md overflow-hidden"
                      >
                        <AccordionHeader
                          onClick={() => handleOpen(tema.id, subtema.id)}
                          className={`px-6 py-3 font-sans text-base transition-all duration-300 ${open[tema.id]?.includes(subtema.id)
                              ? 'bg-[#1a365d] text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                          <div className="flex items-center justify-between w-full">
                            <span>{subtema.nombre}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full mr-2 ${open[tema.id]?.includes(subtema.id)
                                ? 'bg-white/20'
                                : 'bg-gray-300'
                              }`}>
                              {proyectosCount}
                            </span>
                          </div>
                        </AccordionHeader>
                        <AccordionBody className="p-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {data.proyecto
                              .filter((proyecto) => proyecto.subtema_id === subtema.id)
                              .map((proyecto) => {
                                const proyectoDes = data.proyectoDes.find(
                                  (p) => p.id === proyecto.proyecto_id
                                );
                                const rutaImagen = proyectoDes?.ruta_imagen || defaultImage;
                                const descripcionTruncada = truncateText(
                                  proyectoDes?.descripcion || "",
                                  200
                                );

                                return (
                                  <Link
                                    key={proyecto.proyecto_id}
                                    to={`/projectsID/${proyecto.proyecto_id}`}
                                    className="block h-full no-underline"
                                  >
                                    <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full flex flex-col cursor-pointer">
                                      <CardHeader className="relative h-48 m-0 overflow-hidden flex-shrink-0">
                                        <img
                                          src={rutaImagen}
                                          alt="imagen Proyecto"
                                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                        />
                                      </CardHeader>
                                      <CardBody className="p-4 flex-grow">
                                        <Typography
                                          variant="h6"
                                          color="blue-gray"
                                          className="mb-2 font-sans font-bold"
                                        >
                                          {proyectoDes?.titulo}
                                        </Typography>
                                        <Typography className="text-sm font-sans text-gray-600 text-justify">
                                          {descripcionTruncada}
                                        </Typography>
                                      </CardBody>
                                      <CardFooter className="pt-0 px-4 pb-4">
                                        <button className="w-full bg-[#1a365d] hover:bg-[#2d4a7c] text-white font-sans font-medium py-2.5 px-5 rounded-[25px] transition-all duration-300">
                                          Ver más
                                        </button>
                                      </CardFooter>
                                    </Card>
                                  </Link>
                                );
                              })}
                          </div>
                          {data.proyecto.filter((p) => p.subtema_id === subtema.id).length === 0 && (
                            <Typography className="text-gray-500 text-center py-8 font-sans">
                              No hay proyectos en este subtema.
                            </Typography>
                          )}
                        </AccordionBody>
                      </Accordion>
                    );
                  })}
              </div>
            </div>
          ))
        ) : temaActual && (
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-colorPrincipal flex items-center justify-center text-white shadow-lg overflow-hidden">
                {getIconOrImage(temaActual, temaIndex)}
              </div>
              <Typography variant="h5" className="font-bold font-sans text-gray-800">
                {temaActual.nombre}
              </Typography>
            </div>

            {/* Subtemas como acordeones */}
            <div className="space-y-4">
              {data.subtema
                .filter((subtema) => subtema.tema_id === selectedTema)
                .map((subtema) => {
                  const proyectosCount = data.proyecto.filter(p => p.subtema_id === subtema.id).length;
                  return (
                    <Accordion
                      key={subtema.id}
                      open={open[selectedTema]?.includes(subtema.id)}
                      icon={<Icon open={open[selectedTema]?.includes(subtema.id)} />}
                      className="bg-white rounded-xl shadow-md overflow-hidden"
                    >
                      <AccordionHeader
                        onClick={() => handleOpen(selectedTema, subtema.id)}
                        className={`px-6 py-3 font-sans text-base transition-all duration-300 ${open[selectedTema]?.includes(subtema.id)
                            ? 'bg-[#1a365d] text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                      >
                        <div className="flex items-center justify-between w-full">
                          <span>{subtema.nombre}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full mr-2 ${open[selectedTema]?.includes(subtema.id)
                              ? 'bg-white/20'
                              : 'bg-gray-300'
                            }`}>
                            {proyectosCount}
                          </span>
                        </div>
                      </AccordionHeader>
                      <AccordionBody className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {data.proyecto
                            .filter((proyecto) => proyecto.subtema_id === subtema.id)
                            .map((proyecto) => {
                              const proyectoDes = data.proyectoDes.find(
                                (p) => p.id === proyecto.proyecto_id
                              );
                              const rutaImagen = proyectoDes?.ruta_imagen || defaultImage;
                              const descripcionTruncada = truncateText(
                                proyectoDes?.descripcion || "",
                                200
                              );

                              return (
                                <Link
                                  key={proyecto.proyecto_id}
                                  to={`/projectsID/${proyecto.proyecto_id}`}
                                  className="block h-full no-underline"
                                >
                                  <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full flex flex-col cursor-pointer">
                                    <CardHeader className="relative h-48 m-0 overflow-hidden flex-shrink-0">
                                      <img
                                        src={rutaImagen}
                                        alt="imagen Proyecto"
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                      />
                                    </CardHeader>
                                    <CardBody className="p-4 flex-grow">
                                      <Typography
                                        variant="h6"
                                        color="blue-gray"
                                        className="mb-2 font-sans font-bold"
                                      >
                                        {proyectoDes?.titulo}
                                      </Typography>
                                      <Typography className="text-sm font-sans text-gray-600 text-justify">
                                        {descripcionTruncada}
                                      </Typography>
                                    </CardBody>
                                    <CardFooter className="pt-0 px-4 pb-4">
                                      <button className="w-full bg-[#1a365d] hover:bg-[#2d4a7c] text-white font-sans font-medium py-2.5 px-5 rounded-[25px] transition-all duration-300">
                                        Ver más
                                      </button>
                                    </CardFooter>
                                  </Card>
                                </Link>
                              );
                            })}
                        </div>
                        {data.proyecto.filter((p) => p.subtema_id === subtema.id).length === 0 && (
                          <Typography className="text-gray-500 text-center py-8 font-sans">
                            No hay proyectos en este subtema.
                          </Typography>
                        )}
                      </AccordionBody>
                    </Accordion>
                  );
                })}

              {data.subtema.filter((s) => s.tema_id === selectedTema).length === 0 && (
                <div className="bg-white rounded-xl shadow-md p-8 text-center">
                  <Typography className="text-gray-500 font-sans">
                    No hay subtemas en este tema.
                  </Typography>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Projects;
